import { useAnimatedList, Spinner } from '../components/feedback';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { usePoll } from '../hooks/usePoll';
import { api } from '../api/client';
import { ClientTemplateVerifications } from '../components/ClientPackVerification';
import type { ClientModTemplate, ClientModTemplateInput } from '@mcpanel/core';
import { clientModsApi } from '../api/client-mod-templates';
import { errorMessage, useApp } from '../app-context';
import { AddonIcon, AddonSearch } from '../components/AddonSearch';
import { Dialog } from '../components/ui';

export function ClientTemplateEditor({
  initial,
  close,
  saved,
}: {
  initial?: ClientModTemplate;
  close: () => void;
  saved: () => void;
}) {
  const { notify, canOperate } = useApp();
  const [params, setParams] = useSearchParams();
  const tab = params.get('clientTab') === 'errors' ? 'errors' : 'mods';
  const [query, setQuery] = useState('');
  const [generation, setGeneration] = useState(0);
  const changeTab = (value: string) => {
    const next = new URLSearchParams(params);
    next.set('clientTab', value);
    setParams(next);
  };
  // 版を持たない以前のテンプレートは、保存するときに版を選んでもらう
  const start: ClientModTemplateInput = {
    name: initial?.name ?? '',
    mcVersion: initial?.mcVersion ?? '',
    mods: initial?.mods ?? [],
  };
  const [baseline, setBaseline] = useState<ClientModTemplateInput>(start);
  const [form, setForm] = useState<ClientModTemplateInput>(start);
  const modList = useAnimatedList(form.mods, (mod) => mod.projectId);
  // チェックした Mod をまとめて外す・必要性を変える。外した Mod の選択は数えない
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const chosen = form.mods.filter((mod) => selected.has(mod.projectId));
  const setSide = (side: 'required' | 'optional') =>
    setForm((current) => ({
      ...current,
      mods: current.mods.map((mod) => (selected.has(mod.projectId) ? { ...mod, side } : mod)),
    }));
  const removeChosen = () => {
    setForm((current) => ({ ...current, mods: current.mods.filter((mod) => !selected.has(mod.projectId)) }));
    setSelected(new Set());
  };
  const mc = form.mcVersion;
  const setMc = (mcVersion: string) => setForm((current) => ({ ...current, mcVersion }));
  // 検索する版は、Fabric で作れる版の一覧から選ぶ（手で打つと 1.21 と 1.21.1 などを間違えやすい）
  const [versions, setVersions] = useState<string[]>();
  useEffect(() => {
    let cancelled = false;
    void api
      .versions('fabric')
      .then((list) => {
        if (cancelled) return;
        const ids = list.filter((v) => v.availability !== 'unchecked').map((v) => v.id);
        setVersions(ids);
        setForm((current) => (current.mcVersion || initial ? current : { ...current, mcVersion: ids[0] ?? '' }));
      })
      .catch(() => !cancelled && setVersions([]));
    return () => {
      cancelled = true;
    };
  }, []);
  // テストとエラーのタブは自分で進み具合を出すので、Mod タブにいるときだけ確かめる
  const checks = usePoll(
    () => (initial && tab === 'mods' ? clientModsApi.verifications(initial.id) : Promise.resolve([])),
    5000,
    [initial?.id, tab],
  );
  const testing = checks.data?.some((entry) => ['queued', 'running'].includes(entry.verification.state));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      if (initial) await clientModsApi.update(initial.id, form);
      else await clientModsApi.create(form);
      setBaseline(form);
      setGeneration((value) => value + 1);
      notify(initial ? '保存しました。再テストしています' : '作成しました');
      saved();
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={initial ? `テンプレート「${initial.name}」` : 'クライアント Mod テンプレート'}
      wide
      className="template-editor"
      lockResource={initial ? { target: `client-mod-templates:${initial.id}`, screen: 'template' } : undefined}
      revision={initial?.revision}
      onClose={close}
      actions={
        <>
          <button className="btn" onClick={close}>
            キャンセル
          </button>
          {canOperate && (
            <button
              className="btn primary"
              disabled={busy || !form.name.trim() || !form.mcVersion}
              onClick={() => void save()}
            >
              {busy && <Spinner />}
              保存
            </button>
          )}
        </>
      }
    >
      <div className="template-tabs row" role="tablist" aria-label="テンプレート">
        <button className="btn" role="tab" aria-selected={tab === 'mods'} onClick={() => changeTab('mods')}>
          Mod
        </button>
        {initial && (
          <button className="btn" role="tab" aria-selected={tab === 'errors'} onClick={() => changeTab('errors')}>
            テストとエラー
          </button>
        )}
      </div>
      {tab === 'mods' && testing && (
        <p className="alert info" role="status">
          <Spinner />
          このテンプレートの起動テストを実行中です。テスト用 VM がノードのメモリを使います。{' '}
          <Link to="/jobs">ジョブで見る</Link>
        </p>
      )}
      {JSON.stringify(form) !== JSON.stringify(baseline) && (
        <p className="alert info">変更はまだ保存されていません。保存すると対象サーバーを再テストします。</p>
      )}
      {tab === 'errors' && initial ? (
        <ClientTemplateVerifications
          key={generation}
          templateId={initial.id}
          selectedServer={params.get('server') ?? undefined}
          actions={(problem) => {
            const mod = form.mods.find((entry) => entry.projectId === problem.projectId);
            if (!canOperate) return null;
            return (
              <div className="stack">
                {mod && (
                  <div className="row">
                    <button
                      className="btn small"
                      disabled={busy}
                      onClick={() => setForm({ ...form, mods: form.mods.filter((entry) => entry !== mod) })}
                    >
                      {busy && <Spinner />}
                      外す
                    </button>
                    <button
                      className="btn small"
                      disabled={busy || mod.side === 'optional'}
                      onClick={() =>
                        setForm({
                          ...form,
                          mods: form.mods.map((entry) => (entry === mod ? { ...entry, side: 'optional' } : entry)),
                        })
                      }
                    >
                      {busy && <Spinner />}
                      任意にする
                    </button>
                  </div>
                )}
                {problem.projectId && !mod && <span>テンプレートにない Mod（依存または外す予定）</span>}
                {problem.kind === 'dependency' &&
                  problem.dependencyId &&
                  !['minecraft', 'java', 'fabricloader', 'forge', 'neoforge', 'javafml', 'neoforge-language'].includes(
                    problem.dependencyId,
                  ) && (
                    <button
                      className="btn small"
                      onClick={() => {
                        setQuery(problem.dependencyId!);
                        changeTab('mods');
                      }}
                    >
                      足りない Mod を検索して追加
                    </button>
                  )}
                {mod?.side === 'optional' && <p className="small muted">任意です。起動テストには含まれます。</p>}
              </div>
            );
          }}
        />
      ) : (
        <fieldset className="stack template-mod-fields" disabled={!canOperate || busy}>
          <div className="template-meta">
            <div className="field">
              <label htmlFor="client-template-name">名前</label>
              <input
                id="client-template-name"
                className="input"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="client-search-mc">Minecraft の版</label>
              {initial && !initial.mcVersion && (
                <p className="alert warn small">
                  このテンプレートは版の指定がない以前のものです。版を選んで保存すると、その版のサーバーだけで使えます。
                </p>
              )}
              {versions && versions.length === 0 ? (
                <input
                  id="client-search-mc"
                  className="input"
                  placeholder="版の一覧を取れませんでした。例: 1.21.1"
                  value={mc}
                  onChange={(event) => setMc(event.target.value)}
                />
              ) : (
                <select
                  id="client-search-mc"
                  className="input"
                  value={mc}
                  disabled={!versions}
                  onChange={(event) => setMc(event.target.value)}
                >
                  {!versions && <option value="">読み込み中…</option>}
                  {versions && !mc && <option value="">版を選んでください</option>}
                  {mc && versions && !versions.includes(mc) && <option value={mc}>{mc}</option>}
                  {versions?.map((id) => (
                    <option key={id} value={id}>
                      {id}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <p className="small muted">
            Mod ローダーは Fabric です。この版の Fabric 用に作り、同じ版のサーバーだけで選べます。保存するときに、どの
            Mod
            にもこの版の配布ファイルがあるかを確かめます。別の版のサーバー用には、一覧の「別の版で複製」を使ってください。
            Youer・AsyncYouer（NeoForge 系）や Mohist（Forge 系）は Mod と プラグインを一緒に使えるサーバーです。
            これらのクライアント Mod はサーバーの Mod タブで管理します。
          </p>
          <div className="template-mod-columns">
            <section className="template-mod-column" aria-label="入れた Mod">
              <h3>入れた Mod（{form.mods.length}）</h3>
              {form.mods.length === 0 && <p className="small muted">右の検索から追加します。</p>}
              {form.mods.length > 0 && (
                <div className="row template-mod-bulk">
                  <label className="check template-mod-check">
                    <input
                      type="checkbox"
                      aria-label="すべて選ぶ"
                      checked={chosen.length === form.mods.length}
                      ref={(input) => {
                        if (input) input.indeterminate = chosen.length > 0 && chosen.length < form.mods.length;
                      }}
                      onChange={(event) =>
                        setSelected(event.target.checked ? new Set(form.mods.map((mod) => mod.projectId)) : new Set())
                      }
                    />
                    <span className="small muted">{chosen.length ? `${chosen.length} 件を選択中` : 'すべて選ぶ'}</span>
                  </label>
                  <span className="spacer" />
                  <button className="btn small" disabled={!chosen.length} onClick={() => setSide('required')}>
                    必須にする
                  </button>
                  <button className="btn small" disabled={!chosen.length} onClick={() => setSide('optional')}>
                    任意にする
                  </button>
                  <button className="btn small" disabled={!chosen.length} onClick={removeChosen}>
                    外す
                  </button>
                </div>
              )}
              <div className="template-mod-list">
                {modList.items.map((mod) => (
                  <div
                    ref={modList.ref}
                    className={`row template-mod-row ${modList.className(mod)}`}
                    inert={!form.mods.some((item) => item.projectId === mod.projectId)}
                    key={mod.projectId}
                  >
                    <input
                      type="checkbox"
                      aria-label={`${mod.title} を選ぶ`}
                      checked={selected.has(mod.projectId)}
                      onChange={(event) => {
                        const next = new Set(selected);
                        if (event.target.checked) next.add(mod.projectId);
                        else next.delete(mod.projectId);
                        setSelected(next);
                      }}
                    />
                    <AddonIcon url={mod.iconUrl} title={mod.title} size={24} />
                    <strong className="template-mod-title" title={mod.title}>
                      {mod.title}
                    </strong>
                    <select
                      className="input"
                      aria-label={`${mod.title} の必要性`}
                      value={mod.side}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          mods: form.mods.map((entry) =>
                            entry === mod
                              ? { ...entry, side: event.target.value === 'optional' ? 'optional' : 'required' }
                              : entry,
                          ),
                        })
                      }
                    >
                      <option value="required">必須</option>
                      <option value="optional">任意</option>
                    </select>
                    <button
                      className="btn small"
                      onClick={() => setForm({ ...form, mods: form.mods.filter((entry) => entry !== mod) })}
                    >
                      外す
                    </button>
                  </div>
                ))}
              </div>
            </section>
            <section className="template-mod-column" aria-label="Mod を探す">
              <h3>Mod を探す</h3>
              <AddonSearch
                initialQuery={query}
                family="fabric"
                mcVersion={mc}
                sources={['modrinth']}
                target="client"
                stateOf={(hit) => (form.mods.some((mod) => mod.projectId === hit.projectId) ? 'staged' : undefined)}
                onAdd={(hit) =>
                  setForm({
                    ...form,
                    mods: [
                      ...form.mods,
                      {
                        source: 'modrinth',
                        projectId: hit.projectId,
                        slug: hit.slug,
                        title: hit.title,
                        iconUrl: hit.iconUrl?.startsWith('https://') ? hit.iconUrl : undefined,
                        side: 'required',
                      },
                    ],
                  })
                }
              />
            </section>
          </div>
        </fieldset>
      )}
      <p className="small muted">
        任意の Mod
        も配布と起動テストに含まれます。対応ランチャーでは導入を選べます。エラーを解消するには「外す」などで構成を直してください。変更後は保存してください。
      </p>
    </Dialog>
  );
}
